import {
  AfterViewInit,
  Directive,
  ElementRef,
  inject,
  Input,
  NgZone,
  OnDestroy,
} from '@angular/core';
import {
  clampDataTableColumnWidthPx,
  DATA_TABLE_MIN_COL_WIDTH_PX,
} from '../components/ui/data-table/data-table-display.util';

const NON_RESIZABLE_TH_CLASSES = [
  'w-8',
  'w-10',
  'w-12',
  'wh-doc-lines__remove',
  'wmp-col-cart',
  'eq-receipt-lines-table__col-remove',
];

@Directive({
  selector: 'table[appResizableTable]',
})
export class ResizableTableDirective implements AfterViewInit, OnDestroy {
  @Input('appResizableTable') enabled: boolean | '' = true;

  private readonly elRef = inject<ElementRef<HTMLTableElement>>(ElementRef);
  private readonly zone = inject(NgZone);

  private mutationObserver?: MutationObserver;
  private activeDragCleanup: (() => void) | null = null;
  private handleCleanups: Array<() => void> = [];
  private manualWidthsByIndex = new Map<number, number>();
  private userResizedIndices = new Set<number>();

  ngAfterViewInit(): void {
    if (this.enabled === false) return;
    this.zone.runOutsideAngular(() => {
      this.syncHeaderHandles();
      const table = this.elRef.nativeElement;
      if (typeof MutationObserver !== 'undefined') {
        this.mutationObserver = new MutationObserver(() => {
          this.syncHeaderHandles();
        });
        this.mutationObserver.observe(table, {
          childList: true,
          subtree: true,
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.mutationObserver?.disconnect();
    this.mutationObserver = undefined;
    this.activeDragCleanup?.();
    this.activeDragCleanup = null;
    for (const fn of this.handleCleanups) fn();
    this.handleCleanups = [];
  }

  private isHeaderResizable(th: HTMLElement): boolean {
    if (th.hasAttribute('data-no-resize')) return false;
    for (const cls of NON_RESIZABLE_TH_CLASSES) {
      if (th.classList.contains(cls)) return false;
    }
    const labelText = (th.textContent ?? '').trim();
    if (!labelText) {
      return false;
    }
    if (th.querySelector('input[type="checkbox"]') && labelText.length <= 2) {
      return false;
    }
    return true;
  }

  private syncHeaderHandles(): void {
    const table = this.elRef.nativeElement;
    const thEls = Array.from(table.querySelectorAll('thead > tr:first-child > th')) as HTMLElement[];
    thEls.forEach((th, index) => {
      if (!this.isHeaderResizable(th)) return;
      const existing = th.querySelector(':scope > .tt-col-resizer') as HTMLElement | null;
      if (existing) {
        existing.dataset['colIndex'] = String(index);
        return;
      }

      if (typeof window !== 'undefined' && window.getComputedStyle(th).position === 'static') {
        th.style.position = 'relative';
      }

      const handle = document.createElement('span');
      handle.className = 'tt-col-resizer';
      handle.dataset['colIndex'] = String(index);
      handle.setAttribute('role', 'separator');
      handle.setAttribute('aria-orientation', 'vertical');
      const label = (th.textContent ?? '').trim() || `Sloupec ${index + 1}`;
      handle.setAttribute('aria-label', `Změnit šířku sloupce ${label}`);
      handle.title = 'Tažením změníte šířku sloupce, dvojklikem obnovíte výchozí';

      const onMouseDown = (event: MouseEvent): void => {
        const colIdx = Number(handle.dataset['colIndex'] ?? index);
        this.startResize(event, handle, colIdx);
      };
      const onClick = (event: MouseEvent): void => {
        event.preventDefault();
        event.stopPropagation();
      };
      const onDblClick = (event: MouseEvent): void => {
        const colIdx = Number(handle.dataset['colIndex'] ?? index);
        this.resetResize(event, colIdx);
      };

      handle.addEventListener('mousedown', onMouseDown);
      handle.addEventListener('click', onClick);
      handle.addEventListener('dblclick', onDblClick);

      this.handleCleanups.push(() => {
        handle.removeEventListener('mousedown', onMouseDown);
        handle.removeEventListener('click', onClick);
        handle.removeEventListener('dblclick', onDblClick);
      });

      th.appendChild(handle);
    });
  }

  private startResize(event: MouseEvent, handle: HTMLElement, colIndex: number): void {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    this.activeDragCleanup?.();

    const table = this.elRef.nativeElement;
    const thEls = Array.from(table.querySelectorAll('thead > tr:first-child > th')) as HTMLElement[];
    const targetTh = thEls[colIndex];
    if (!targetTh) return;

    const currentWidth =
      targetTh.getBoundingClientRect().width ||
      this.manualWidthsByIndex.get(colIndex) ||
      120;
    const startWidth = clampDataTableColumnWidthPx(currentWidth);
    const startX = event.clientX;

    for (let i = 0; i < colIndex; i++) {
      if (!this.manualWidthsByIndex.has(i) && thEls[i] && this.isHeaderResizable(thEls[i])) {
        const w = thEls[i].getBoundingClientRect().width;
        if (w > 0) {
          this.manualWidthsByIndex.set(i, clampDataTableColumnWidthPx(w));
        }
      }
    }
    this.manualWidthsByIndex.set(colIndex, startWidth);
    this.userResizedIndices.add(colIndex);

    table.classList.add('tt-table--resizing');
    handle.classList.add('tt-col-resizer--active', 'tt-col-resizer--customized');
    if (typeof document !== 'undefined') {
      document.body.classList.add('tt-col-resizing');
    }

    this.applyWidths(thEls);

    const onMouseMove = (moveEvent: MouseEvent): void => {
      const deltaX = moveEvent.clientX - startX;
      const nextWidth = clampDataTableColumnWidthPx(startWidth + deltaX);
      if (this.manualWidthsByIndex.get(colIndex) === nextWidth) return;
      this.manualWidthsByIndex.set(colIndex, nextWidth);
      this.applyWidths(thEls);
    };

    const onMouseUp = (): void => {
      this.activeDragCleanup?.();
      this.activeDragCleanup = null;
    };

    window.addEventListener('mousemove', onMouseMove, { passive: true });
    window.addEventListener('mouseup', onMouseUp);

    this.activeDragCleanup = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      table.classList.remove('tt-table--resizing');
      handle.classList.remove('tt-col-resizer--active');
      if (typeof document !== 'undefined') {
        document.body.classList.remove('tt-col-resizing');
      }
    };
  }

  private resetResize(event: MouseEvent, colIndex: number): void {
    event.preventDefault();
    event.stopPropagation();
    this.activeDragCleanup?.();
    this.activeDragCleanup = null;

    const table = this.elRef.nativeElement;
    const thEls = Array.from(table.querySelectorAll('thead > tr:first-child > th')) as HTMLElement[];

    if (event.shiftKey || !this.manualWidthsByIndex.has(colIndex)) {
      this.manualWidthsByIndex.clear();
      this.userResizedIndices.clear();
    } else {
      this.userResizedIndices.delete(colIndex);
      this.manualWidthsByIndex.delete(colIndex);
      if (this.userResizedIndices.size === 0) {
        this.manualWidthsByIndex.clear();
      }
    }

    this.applyWidths(thEls);
  }

  private applyWidths(thEls: HTMLElement[]): void {
    const table = this.elRef.nativeElement;
    const hasManual = this.manualWidthsByIndex.size > 0;
    table.style.tableLayout = hasManual ? 'fixed' : '';

    let minTablePx = 0;
    thEls.forEach((th, idx) => {
      const px = this.manualWidthsByIndex.get(idx);
      const val = px != null && px > 0 ? `${px}px` : '';
      th.style.width = val;
      th.style.minWidth = val;
      th.style.maxWidth = val;
      const handle = th.querySelector(':scope > .tt-col-resizer');
      handle?.classList.toggle('tt-col-resizer--customized', this.userResizedIndices.has(idx));
      if (px != null && px > 0) {
        minTablePx += px;
      } else {
        minTablePx += DATA_TABLE_MIN_COL_WIDTH_PX;
      }
    });

    for (const row of Array.from(table.tBodies[0]?.rows ?? [])) {
      if (row.cells.length !== thEls.length) continue;
      thEls.forEach((_th, idx) => {
        const px = this.manualWidthsByIndex.get(idx);
        const val = px != null && px > 0 ? `${px}px` : '';
        const td = row.cells[idx];
        if (td) {
          td.style.width = val;
          td.style.minWidth = val;
          td.style.maxWidth = val;
        }
      });
    }

    table.style.minWidth = hasManual && minTablePx > 0 ? `${minTablePx}px` : '';
  }
}
